export type TimeLogDurationInput = string | number | null | undefined;

export const DEFAULT_TIME_LOG_ROUNDING_MINUTES = 15;

export const parseTimeLogDurationToMinutes = (
    duration: TimeLogDurationInput,
): number => {
    if (duration === undefined || duration === null) {
        return 0;
    }

    if (typeof duration === "number") {
        return Number.isFinite(duration) ? duration : 0;
    }

    const value = duration.trim();
    if (!value) {
        return 0;
    }

    const timeSpanMatch = value.match(
        /^(-)?(?:(\d+)\.)?(\d+):(\d{1,2})(?::(\d{1,2})(?:\.\d+)?)?$/,
    );
    if (timeSpanMatch) {
        const sign = timeSpanMatch[1] ? -1 : 1;
        const days = Number(timeSpanMatch[2] ?? 0);
        const hours = Number(timeSpanMatch[3] ?? 0);
        const minutes = Number(timeSpanMatch[4] ?? 0);
        const seconds = Number(timeSpanMatch[5] ?? 0);

        return sign * (days * 24 * 60 + hours * 60 + minutes + seconds / 60);
    }

    const numeric = Number(value.replace(",", "."));
    return Number.isFinite(numeric) ? numeric : 0;
};

export const formatMinutesAsHHmm = (minutes: number): string => {
    const roundedMinutes = Math.max(0, Math.round(minutes));
    const hours = Math.floor(roundedMinutes / 60);
    const remainderMinutes = roundedMinutes % 60;

    return `${String(hours).padStart(2, "0")}:${String(remainderMinutes).padStart(
        2,
        "0",
    )}`;
};

export const normalizeTimeLogRoundingMinutes = (
    roundingMinutes?: number | null,
): number => {
    if (roundingMinutes === undefined || roundingMinutes === null) {
        return DEFAULT_TIME_LOG_ROUNDING_MINUTES;
    }

    if (!Number.isFinite(roundingMinutes)) {
        return DEFAULT_TIME_LOG_ROUNDING_MINUTES;
    }

    return roundingMinutes <= 0 ? 0 : Math.round(roundingMinutes);
};

export const userTimeLogRoundingMinutes = (user?: {
    supportTimeLogRoundingMinutes?: number | null;
}): number =>
    normalizeTimeLogRoundingMinutes(user?.supportTimeLogRoundingMinutes);

export const roundMinutesUpToInterval = (
    minutes: number,
    roundingMinutes?: number | null,
): number => {
    const interval = normalizeTimeLogRoundingMinutes(roundingMinutes);
    const positiveMinutes = Math.max(0, minutes);

    if (interval === 0) {
        return positiveMinutes;
    }

    return Math.max(interval, Math.ceil(positiveMinutes / interval) * interval);
};

export const roundMinutesUpToQuarterHour = (minutes: number): number => {
    return roundMinutesUpToInterval(minutes, DEFAULT_TIME_LOG_ROUNDING_MINUTES);
};

export const suggestTimeLogDuration = (
    duration: TimeLogDurationInput,
    roundingMinutes?: number | null,
): string =>
    formatMinutesAsHHmm(
        roundMinutesUpToInterval(
            parseTimeLogDurationToMinutes(duration),
            roundingMinutes,
        ),
    );

export const roundTimeLogDurationUpToQuarterHour = (
    duration: TimeLogDurationInput,
): string =>
    suggestTimeLogDuration(duration, DEFAULT_TIME_LOG_ROUNDING_MINUTES);
